/**
 * Agent Registry — hooks (queries + selected-client state).
 *
 * The "selected client" is a global picked-agent that the TopNav switcher owns
 * and pages consume (Threat Hunting, FIM per-agent views, etc). Persisted to
 * localStorage so a page reload keeps the choice; broadcast via a tiny event
 * bus so every subscriber updates without prop drilling.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useSyncExternalStore } from "react";
import {
  listRegistryAgents,
  listPickerAgents,
  getRegistrySummary,
  getRegistryFilters,
  getRegistryAgent,
} from "../api/registry.api";
import type { RegistryQuery } from "../api/registry.schemas";

/* ---------- query keys ---------- */

export const registryKeys = {
  all: ["registry"] as const,
  org: (orgId: string) => [...registryKeys.all, orgId] as const,
  list: (orgId: string, q: RegistryQuery) =>
    [...registryKeys.org(orgId), "list", q] as const,
  summary: (orgId: string) => [...registryKeys.org(orgId), "summary"] as const,
  filters: (orgId: string) => [...registryKeys.org(orgId), "filters"] as const,
  agent: (orgId: string, agentIdOrHost: string) =>
    [...registryKeys.org(orgId), "agent", agentIdOrHost] as const,
  picker: (orgId: string) => [...registryKeys.org(orgId), "picker"] as const,
};

/* ---------- queries ---------- */

export function useRegistryAgents(orgId: string, query: RegistryQuery = {}) {
  return useQuery({
    queryKey: registryKeys.list(orgId, query),
    queryFn: () => listRegistryAgents(orgId, query),
    enabled: orgId !== "",
    // Keep the previous page visible while paginating.
    placeholderData: (prev) => prev,
    staleTime: 15_000,
  });
}

export function useRegistrySummary(orgId: string) {
  return useQuery({
    queryKey: registryKeys.summary(orgId),
    queryFn: () => getRegistrySummary(orgId),
    enabled: orgId !== "",
    staleTime: 30_000,
  });
}

export function useRegistryFilters(orgId: string) {
  return useQuery({
    queryKey: registryKeys.filters(orgId),
    queryFn: () => getRegistryFilters(orgId),
    enabled: orgId !== "",
    staleTime: 60_000,
  });
}

export function useRegistryAgent(orgId: string, agentIdOrHost: string | null) {
  return useQuery({
    queryKey: agentIdOrHost
      ? registryKeys.agent(orgId, agentIdOrHost)
      : ["registry", "agent", "none"],
    queryFn: () => getRegistryAgent(orgId, agentIdOrHost as string),
    enabled: orgId !== "" && agentIdOrHost !== null,
    staleTime: 30_000,
  });
}

/* ---------- selected client (persisted + broadcast) ---------- */

export interface SelectedClient {
  agent_id: string;
  hostname: string;
}

const STORAGE_KEY = "specta.selectedClient";
const CHANGE_EVENT = "specta:selectedClient";

function readRaw(): string | null {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}

/* ─── useSyncExternalStore plumbing ──────────────────────────────────
 *
 * We used to use `useState` + a `useEffect` listener. That pattern was
 * correct in principle but suffered subtle timing issues under React
 * 18's concurrent rendering: a `select()` call in one component would
 * dispatch the custom event, and SOME subscribed components would
 * re-render while OTHERS stayed on the stale state. The symptom was
 * "TopNav + Agent Dashboard update after an org-triggered agent swap,
 * but FIM + SysMon + Auth don't".
 *
 * `useSyncExternalStore` is the official React 18 primitive for
 * exactly this scenario — a value that lives OUTSIDE React but needs
 * to be read by every component consistently. It gives the strongest
 * tearing-safety guarantees React offers and removes the subscriber
 * management from userland.
 *
 * We cache the parsed object by its raw-string identity so the
 * snapshot function returns a REFERENTIALLY STABLE value when nothing
 * has changed. Without this cache, every render would deserialize and
 * return a fresh object, making React think the state had changed on
 * every render and spam re-renders.
 */
let cachedRaw: string | null = null;
let cachedParsed: SelectedClient | null = null;

function readSelected(): SelectedClient | null {
  const raw = readRaw();
  if (raw === cachedRaw) return cachedParsed;
  cachedRaw = raw;
  try {
    cachedParsed = raw ? (JSON.parse(raw) as SelectedClient) : null;
  } catch {
    cachedParsed = null;
  }
  return cachedParsed;
}

/** Subscribers list — mutations within this tab need our own notify()
 *  because `storage` events only fire cross-tab. Any `select()` call
 *  tells every mounted useSelectedClient (and anyone else subscribed)
 *  that the value changed. */
const listeners = new Set<() => void>();
const notify = (): void => listeners.forEach((fn) => fn());

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  const onCustom = () => cb();
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb();
  };
  window.addEventListener(CHANGE_EVENT, onCustom);
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener(CHANGE_EVENT, onCustom);
    window.removeEventListener("storage", onStorage);
  };
}

/** SSR snapshot — localStorage is unavailable during server render,
 *  so return null. Only matters if this app ever gets SSR; harmless
 *  otherwise. */
const getServerSnapshot = (): SelectedClient | null => null;

/**
 * Global selected-client hook. Reads localStorage, subscribes to the
 * change event so cross-component picks propagate, and exposes a setter
 * that persists + broadcasts. Public API identical to the previous
 * useState-based implementation.
 */
export function useSelectedClient() {
  const selected = useSyncExternalStore(subscribe, readSelected, getServerSnapshot);

  const select = useCallback((c: SelectedClient | null) => {
    // The exact string we're about to store (or null when clearing).
    const raw = c ? JSON.stringify(c) : null;
    try {
      if (raw !== null) localStorage.setItem(STORAGE_KEY, raw);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable — still fire the event so this session updates */
    }
    // Keep the module cache in sync with exactly what we just wrote, so the
    // next readSelected() returns the new value — INCLUDING when clearing.
    //
    // The old code set `cachedRaw = null` here to "invalidate" the cache.
    // That worked when SETTING an agent (the new raw string differed from
    // null, so readSelected re-parsed) but broke when CLEARING: after a
    // clear, localStorage reads back as null too, so readSelected's
    // `raw === cachedRaw` check (null === null) short-circuited and returned
    // the PREVIOUS agent. The selection looked erased in localStorage but
    // the app kept showing the old agent — e.g. switching to an org with no
    // agents didn't visibly deselect, and the agent "wouldn't change".
    // Writing both halves of the cache explicitly avoids that collision.
    cachedRaw = raw;
    cachedParsed = c;
    // Fire both the custom event AND the storage event. Our subscribe()
    // listens to both, but any OTHER code in the codebase might only
    // listen to one — belt-and-braces guarantees cross-tab AND same-tab
    // propagation.
    window.dispatchEvent(new Event(CHANGE_EVENT));
    notify();
  }, []);

  return { selected, select };
}

/* ---------- prefetch helper for the TopNav switcher ----------
   The switcher wants a quick list on first open; using useMutation lets it
   defer the fetch until the dropdown opens, without turning the topnav into
   a hook that fires on every render. */

/** Lean picker query for the TopNav agent selector — 4 fields only. */
export function usePickerAgents(orgId: string, search?: string) {
  return useQuery({
    queryKey: [...registryKeys.picker(orgId), search ?? ""],
    queryFn: () => listPickerAgents(orgId, { search, limit: 50 }),
    enabled: orgId !== "",
    staleTime: 30_000,
  });
}


// /**
//  * Agent Registry — hooks (queries + selected-client state).
//  *
//  * The "selected client" is a global picked-agent that the TopNav switcher owns
//  * and pages consume (Threat Hunting, FIM per-agent views, etc). Persisted to
//  * localStorage so a page reload keeps the choice; broadcast via a tiny event
//  * bus so every subscriber updates without prop drilling.
//  */
// import { useQuery } from "@tanstack/react-query";
// import { useCallback, useSyncExternalStore } from "react";
// import {
//   listRegistryAgents,
//   listPickerAgents,
//   getRegistrySummary,
//   getRegistryFilters,
//   getRegistryAgent,
// } from "../api/registry.api";
// import type { RegistryQuery } from "../api/registry.schemas";

// /* ---------- query keys ---------- */

// export const registryKeys = {
//   all: ["registry"] as const,
//   org: (orgId: string) => [...registryKeys.all, orgId] as const,
//   list: (orgId: string, q: RegistryQuery) =>
//     [...registryKeys.org(orgId), "list", q] as const,
//   summary: (orgId: string) => [...registryKeys.org(orgId), "summary"] as const,
//   filters: (orgId: string) => [...registryKeys.org(orgId), "filters"] as const,
//   agent: (orgId: string, agentIdOrHost: string) =>
//     [...registryKeys.org(orgId), "agent", agentIdOrHost] as const,
//   picker: (orgId: string) => [...registryKeys.org(orgId), "picker"] as const,
// };

// /* ---------- queries ---------- */

// export function useRegistryAgents(orgId: string, query: RegistryQuery = {}) {
//   return useQuery({
//     queryKey: registryKeys.list(orgId, query),
//     queryFn: () => listRegistryAgents(orgId, query),
//     enabled: orgId !== "",
//     // Keep the previous page visible while paginating.
//     placeholderData: (prev) => prev,
//     staleTime: 15_000,
//   });
// }

// export function useRegistrySummary(orgId: string) {
//   return useQuery({
//     queryKey: registryKeys.summary(orgId),
//     queryFn: () => getRegistrySummary(orgId),
//     enabled: orgId !== "",
//     staleTime: 30_000,
//   });
// }

// export function useRegistryFilters(orgId: string) {
//   return useQuery({
//     queryKey: registryKeys.filters(orgId),
//     queryFn: () => getRegistryFilters(orgId),
//     enabled: orgId !== "",
//     staleTime: 60_000,
//   });
// }

// export function useRegistryAgent(orgId: string, agentIdOrHost: string | null) {
//   return useQuery({
//     queryKey: agentIdOrHost
//       ? registryKeys.agent(orgId, agentIdOrHost)
//       : ["registry", "agent", "none"],
//     queryFn: () => getRegistryAgent(orgId, agentIdOrHost as string),
//     enabled: orgId !== "" && agentIdOrHost !== null,
//     staleTime: 30_000,
//   });
// }

// /* ---------- selected client (persisted + broadcast) ---------- */

// export interface SelectedClient {
//   agent_id: string;
//   hostname: string;
// }

// const STORAGE_KEY = "specta.selectedClient";
// const CHANGE_EVENT = "specta:selectedClient";

// function readRaw(): string | null {
//   try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
// }

// /* ─── useSyncExternalStore plumbing ──────────────────────────────────
//  *
//  * We used to use `useState` + a `useEffect` listener. That pattern was
//  * correct in principle but suffered subtle timing issues under React
//  * 18's concurrent rendering: a `select()` call in one component would
//  * dispatch the custom event, and SOME subscribed components would
//  * re-render while OTHERS stayed on the stale state. The symptom was
//  * "TopNav + Agent Dashboard update after an org-triggered agent swap,
//  * but FIM + SysMon + Auth don't".
//  *
//  * `useSyncExternalStore` is the official React 18 primitive for
//  * exactly this scenario — a value that lives OUTSIDE React but needs
//  * to be read by every component consistently. It gives the strongest
//  * tearing-safety guarantees React offers and removes the subscriber
//  * management from userland.
//  *
//  * We cache the parsed object by its raw-string identity so the
//  * snapshot function returns a REFERENTIALLY STABLE value when nothing
//  * has changed. Without this cache, every render would deserialize and
//  * return a fresh object, making React think the state had changed on
//  * every render and spam re-renders.
//  */
// let cachedRaw: string | null = null;
// let cachedParsed: SelectedClient | null = null;

// function readSelected(): SelectedClient | null {
//   const raw = readRaw();
//   if (raw === cachedRaw) return cachedParsed;
//   cachedRaw = raw;
//   try {
//     cachedParsed = raw ? (JSON.parse(raw) as SelectedClient) : null;
//   } catch {
//     cachedParsed = null;
//   }
//   return cachedParsed;
// }

// /** Subscribers list — mutations within this tab need our own notify()
//  *  because `storage` events only fire cross-tab. Any `select()` call
//  *  tells every mounted useSelectedClient (and anyone else subscribed)
//  *  that the value changed. */
// const listeners = new Set<() => void>();
// const notify = (): void => listeners.forEach((fn) => fn());

// function subscribe(cb: () => void): () => void {
//   listeners.add(cb);
//   const onCustom = () => cb();
//   const onStorage = (e: StorageEvent) => {
//     if (e.key === STORAGE_KEY) cb();
//   };
//   window.addEventListener(CHANGE_EVENT, onCustom);
//   window.addEventListener("storage", onStorage);
//   return () => {
//     listeners.delete(cb);
//     window.removeEventListener(CHANGE_EVENT, onCustom);
//     window.removeEventListener("storage", onStorage);
//   };
// }

// /** SSR snapshot — localStorage is unavailable during server render,
//  *  so return null. Only matters if this app ever gets SSR; harmless
//  *  otherwise. */
// const getServerSnapshot = (): SelectedClient | null => null;

// /**
//  * Global selected-client hook. Reads localStorage, subscribes to the
//  * change event so cross-component picks propagate, and exposes a setter
//  * that persists + broadcasts. Public API identical to the previous
//  * useState-based implementation.
//  */
// export function useSelectedClient() {
//   const selected = useSyncExternalStore(subscribe, readSelected, getServerSnapshot);

//   const select = useCallback((c: SelectedClient | null) => {
//     try {
//       if (c) localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
//       else localStorage.removeItem(STORAGE_KEY);
//     } catch {
//       /* storage unavailable — still fire the event so this session updates */
//     }
//     // Invalidate the parsed cache so the next readSelected() returns the
//     // fresh value instead of the stale cached parse.
//     cachedRaw = null;
//     // Fire both the custom event AND the storage event. Our subscribe()
//     // listens to both, but any OTHER code in the codebase might only
//     // listen to one — belt-and-braces guarantees cross-tab AND same-tab
//     // propagation.
//     window.dispatchEvent(new Event(CHANGE_EVENT));
//     notify();
//   }, []);

//   return { selected, select };
// }

// /* ---------- prefetch helper for the TopNav switcher ----------
//    The switcher wants a quick list on first open; using useMutation lets it
//    defer the fetch until the dropdown opens, without turning the topnav into
//    a hook that fires on every render. */

// /** Lean picker query for the TopNav agent selector — 4 fields only. */
// export function usePickerAgents(orgId: string, search?: string) {
//   return useQuery({
//     queryKey: [...registryKeys.picker(orgId), search ?? ""],
//     queryFn: () => listPickerAgents(orgId, { search, limit: 50 }),
//     enabled: orgId !== "",
//     staleTime: 30_000,
//   });
// }
